import { useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchPublicReport } from "../../../api/service/report";
import { HtmlTextFormatter, dateToString } from "../../../components/Utils";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceEmpty } from "../../../components/utils/ResourceEmpty";
import { ResourceError } from "../../../components/utils/ResourceError";
import { DocumentViewer } from "../../../components/dashboard/Media/documentViewer";

const statusLabels = {
  1: "Aktiv",
  2: "Fullført",
  3: "Fakturert",
  4: "Kansellert",
};

const PublicSection = ({ title, children }) => (
  <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="border-b border-slate-100 px-6 py-4">
      <h2 className="text-base font-semibold text-slate-900">{title}</h2>
    </div>
    <div className="px-6 py-5">{children}</div>
  </section>
);

const CheckIcon = () => (
  <svg className="h-5 w-5 text-emerald-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
  </svg>
);

const DeviationIcon = () => (
  <svg className="h-5 w-5 text-amber-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
  </svg>
);

const PendingIcon = () => (
  <svg className="h-5 w-5 text-slate-300 shrink-0" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
  </svg>
);

export const PublicReportCustomerView = () => {
  const { token } = useParams();
  const accessToken = useSelector((state) => state.session.accessToken);
  const currentUser = useSelector((state) => state.session.currentUser);
  const [state, setState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    report: null,
  });

  useEffect(() => {
    const loadReport = async () => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        error: false,
        errors: [],
      }));

      try {
        const response = await fetchPublicReport(token);
        setState({
          isLoading: false,
          error: false,
          errors: [],
          report: response?.data?.data || null,
        });
      } catch (error) {
        setState({
          isLoading: false,
          error: true,
          errors: error?.response?.data?.errors || ["Kunne ikke laste rapporten"],
          report: null,
        });
      }
    };

    if (token) {
      loadReport();
    }
  }, [token]);

  const report = state.report;

  const groupedVariables = useMemo(() => {
    if (!report?.variables?.length) return [];
    const groups = {};
    const typeGroups = {};

    (report.variable_type_groups || []).forEach((tg) => {
      typeGroups[tg.id] = tg;
    });
    (report.variable_groups || []).forEach((g) => {
      groups[g.id] = g;
    });

    const byTypeGroup = {};
    const ungrouped = [];

    report.variables.forEach((v) => {
      if (v.type_group_id && typeGroups[v.type_group_id]) {
        const key = v.type_group_id;
        if (!byTypeGroup[key]) byTypeGroup[key] = { group: typeGroups[key], variables: [] };
        byTypeGroup[key].variables.push(v);
      } else if (v.group_id && groups[v.group_id]) {
        const key = `g_${v.group_id}`;
        if (!byTypeGroup[key]) byTypeGroup[key] = { group: groups[v.group_id], variables: [] };
        byTypeGroup[key].variables.push(v);
      } else {
        ungrouped.push(v);
      }
    });

    const result = Object.values(byTypeGroup);
    if (ungrouped.length) result.push({ group: null, variables: ungrouped });
    return result;
  }, [report]);

  if (accessToken && currentUser?.id && report?.id) {
    return <Navigate to={`/dashboard/service/reports/${report.id}`} replace />;
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#dff5ff,_#f8fafc_45%,_#eef2f7)] px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white/90 shadow-xl backdrop-blur">
          <div
            className="px-6 py-8 sm:px-8"
            style={{
              background: `linear-gradient(135deg, ${report?.company?.main_color || "#0ea5e9"} 0%, #0f172a 100%)`,
            }}
          >
            <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
              <div className="max-w-3xl">
                <p className="text-sm font-medium uppercase tracking-[0.2em] text-white/70">
                  Kundevisning
                </p>
                <h1 className="mt-3 text-3xl font-semibold tracking-tight text-white">
                  {report?.displayTitle || "Rapport"}
                </h1>
                <p className="mt-3 max-w-2xl text-sm text-white/80">
                  Denne visningen er delt direkte fra Promang og viser informasjon om rapporten.
                </p>
              </div>

              {report?.company?.logo_url ? (
                <img
                  src={report.company.logo_url}
                  alt={report.company.name || "Firma"}
                  className="h-14 w-auto rounded-lg bg-white/95 p-2"
                />
              ) : (
                <div className="rounded-xl bg-white/10 px-4 py-3 text-sm font-medium text-white">
                  {report?.company?.name || "Promang"}
                </div>
              )}
            </div>

            {report && (
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="rounded-full bg-white/15 px-3 py-1.5 text-sm text-white">
                  Status: {statusLabels[report.status] || report.status || "-"}
                </span>
                {report?.report_type?.title ? (
                  <span className="rounded-full bg-white/15 px-3 py-1.5 text-sm text-white">
                    Type: {report.report_type.title}
                  </span>
                ) : null}
              </div>
            )}
          </div>

          <div className="space-y-6 px-4 py-6 sm:px-8 sm:py-8">
            {state.isLoading ? <ResourceLoading /> : null}
            {!state.isLoading && state.error ? (
              <ResourceError errors={state.errors} />
            ) : null}
            {!state.isLoading && !state.error && !report ? <ResourceEmpty /> : null}

            {!state.isLoading && !state.error && report ? (
              <div className="space-y-6">
                {/* Top grid: overview + sidebar */}
                <div className="grid gap-6 lg:grid-cols-3">
                  <div className="space-y-6 lg:col-span-2">
                    <PublicSection title="Oversikt">
                      <dl className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            Rapport
                          </dt>
                          <dd className="mt-1 text-sm text-slate-900">
                            {report.displayTitle || "-"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            Kunde
                          </dt>
                          <dd className="mt-1 text-sm text-slate-900">
                            {report.customer?.displayTitle || report.customer?.name || "-"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            Kontakt
                          </dt>
                          <dd className="mt-1 text-sm text-slate-900">
                            {report.contact?.displayTitle || report.contact?.name || "-"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            Utførende
                          </dt>
                          <dd className="mt-1 text-sm text-slate-900">
                            {report.executing_employee?.displayTitle || report.executing_employee?.name || "-"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            Avtalt tidspunkt
                          </dt>
                          <dd className="mt-1 text-sm text-slate-900">
                            {report.appointment ? dateToString(report.appointment) : "-"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            Fullført dato
                          </dt>
                          <dd className="mt-1 text-sm text-slate-900">
                            {report.finished_date ? dateToString(report.finished_date) : "-"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            Registrert
                          </dt>
                          <dd className="mt-1 text-sm text-slate-900">
                            {report.created_at ? dateToString(report.created_at) : "-"}
                          </dd>
                        </div>
                        {report.system ? (
                          <div>
                            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                              Anlegg
                            </dt>
                            <dd className="mt-1 text-sm text-slate-900">
                              {report.system.displayTitle || "-"}
                            </dd>
                          </div>
                        ) : null}
                      </dl>
                    </PublicSection>

                    {report.delivery_address ? (
                      <PublicSection title="Adresse">
                        <p className="text-sm text-slate-700">
                          {[
                            report.delivery_address.address,
                            report.delivery_address.zip,
                            report.delivery_address.city,
                          ]
                            .filter(Boolean)
                            .join(", ") || "-"}
                        </p>
                      </PublicSection>
                    ) : null}

                    {report.report ? (
                      <PublicSection title="Rapport">
                        <div className="prose prose-sm max-w-none text-slate-700">
                          <HtmlTextFormatter string={report.report} />
                        </div>
                      </PublicSection>
                    ) : null}
                  </div>

                  <div className="space-y-6 lg:col-span-1">
                    <PublicSection title="Om lenken">
                      <div className="space-y-3 text-sm text-slate-600">
                        <p>
                          Denne lenken gir direkte tilgang til kundevisning av rapporten.
                        </p>
                        <p>
                          Hvis du er ansatt og allerede logget inn i Promang, blir du sendt videre til den interne rapportsiden.
                        </p>
                      </div>
                    </PublicSection>

                    {report.description ? (
                      <PublicSection title="Beskrivelse">
                        <div className="prose prose-sm max-w-none text-slate-700">
                          <HtmlTextFormatter string={report.description} />
                        </div>
                      </PublicSection>
                    ) : null}

                    {report.must_be_rectified ? (
                      <PublicSection title="Må utbedres">
                        <p className="text-sm font-medium text-amber-600">
                          Denne rapporten inneholder punkter som må utbedres.
                        </p>
                      </PublicSection>
                    ) : null}
                  </div>
                </div>

                {/* Checklist — full width */}
                {report.checklist ? (
                  <PublicSection title={`Sjekkliste${report.checklist.title ? ": " + report.checklist.title : ""}`}>
                    {report.checklist.description ? (
                      <p className="mb-4 text-sm text-slate-600">{report.checklist.description}</p>
                    ) : null}
                    {report.checklist.items?.length ? (
                      <div className="divide-y divide-slate-100">
                        {report.checklist.items.map((item) => (
                          <div key={item.id} className="py-4 first:pt-0 last:pb-0">
                            <div className="flex items-start gap-3">
                              {item.isDeviation ? (
                                <DeviationIcon />
                              ) : item.isApproved ? (
                                <CheckIcon />
                              ) : (
                                <PendingIcon />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-slate-900">{item.title}</p>
                                {item.description ? (
                                  <p className="mt-1 text-sm text-slate-600">{item.description}</p>
                                ) : null}
                                {item.isDeviation && item.deviation_description ? (
                                  <p className="mt-1 text-sm text-amber-700">
                                    <span className="font-medium">Avvik: </span>
                                    {item.deviation_description}
                                  </p>
                                ) : null}
                                {item.notice ? (
                                  <p className="mt-1 text-sm text-slate-500">
                                    <span className="font-medium">Merknad: </span>
                                    {item.notice}
                                  </p>
                                ) : null}
                                {item.date_completed ? (
                                  <p className="mt-1 text-xs text-slate-400">
                                    Fullført: {dateToString(item.date_completed)}
                                  </p>
                                ) : null}
                                {item.documents?.length ? (
                                  <div className="mt-3">
                                    <DocumentViewer
                                      pathType="quickview"
                                      documents={item.documents}
                                    />
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">Ingen punkter i sjekklisten.</p>
                    )}
                  </PublicSection>
                ) : null}

                {/* Variables — full width */}
                {report.variables?.length ? (
                  <PublicSection title="Variabler">
                    <div className="space-y-6">
                      {groupedVariables.map((section, idx) => (
                        <div key={idx}>
                          {section.group ? (
                            <h3 className="mb-3 text-sm font-semibold text-slate-700">
                              {section.group.title}
                            </h3>
                          ) : null}
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-slate-200">
                                  <th className="pb-2 pr-4 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                    Type
                                  </th>
                                  <th className="pb-2 pr-4 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                    Ref.
                                  </th>
                                  <th className="pb-2 pr-4 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                    Verdi 1
                                  </th>
                                  <th className="pb-2 pr-4 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                    Verdi 2
                                  </th>
                                  <th className="pb-2 pr-4 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                    Verdi 3
                                  </th>
                                  <th className="pb-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                    Verdi 4
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {section.variables.map((v) => (
                                  <tr key={v.id}>
                                    <td className="py-2 pr-4 text-slate-900">
                                      {v.variable_type?.title || "-"}
                                    </td>
                                    <td className="py-2 pr-4 text-slate-600">{v.reference || "-"}</td>
                                    <td className="py-2 pr-4 text-slate-600">
                                      {v.field1 != null ? `${v.field1}${v.unit?.title ? " " + v.unit.title : ""}` : "-"}
                                    </td>
                                    <td className="py-2 pr-4 text-slate-600">
                                      {v.field2 != null ? `${v.field2}${v.measurement_unit?.title ? " " + v.measurement_unit.title : ""}` : "-"}
                                    </td>
                                    <td className="py-2 pr-4 text-slate-600">{v.field3 || "-"}</td>
                                    <td className="py-2 text-slate-600">{v.field4 || "-"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {/* Measurements for variables in this group */}
                          {section.variables.some((v) => v.measurements?.length) ? (
                            <div className="mt-4 space-y-3">
                              {section.variables
                                .filter((v) => v.measurements?.length)
                                .map((v) => (
                                  <div key={`m_${v.id}`}>
                                    <p className="mb-1 text-xs font-medium text-slate-500">
                                      Målinger — {v.variable_type?.title || v.displayTitle}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      {v.measurements.map((m) => (
                                        <span
                                          key={m.id}
                                          className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700"
                                        >
                                          {m.value}{v.measurement_unit?.title ? " " + v.measurement_unit.title : ""}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </PublicSection>
                ) : null}

                {/* Report-level documents — full width */}
                {report.documents?.length ? (
                  <PublicSection title="Vedlegg">
                    <DocumentViewer pathType="quickview" documents={report.documents} />
                  </PublicSection>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
